import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Pencil,
  Save,
  X,
  AlertCircle,
  KeyRound,
  Building2,
  Ban,
  ShieldCheck,
} from 'lucide-react';
import { type Role } from '@hcc/types';
import { Button } from '../../../components/ui/Button';
import { MultiSelect } from '../../../components/ui/MultiSelect';
import {
  UserMobilisationsRead,
  UserMobilisationsEdit,
  type MobLite,
} from '../../../components/admin/UserMobilisations';
import { api, ApiError } from '../../../lib/api';
import { ROLE_GROUPS, roleLabel, sameSet, type AdminUser, type PersonLite } from './roles';

export function UserRow({
  user,
  allMobs,
  isMe,
  isEditing,
  onEdit,
  onCancel,
  onSaved,
}: {
  user: AdminUser;
  allMobs: MobLite[];
  isMe: boolean;
  isEditing: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSaved: (u: AdminUser) => void;
}) {
  return (
    <tr className="border-t border-line/10">
      <td className="px-4 py-3 align-top">
        <div className="flex flex-wrap items-center gap-2 font-medium">
          <span>{user.display_name}</span>
          {isMe ? (
            <span className="rounded-full bg-success-soft/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-success-soft-fg">
              You
            </span>
          ) : null}
          {user.disabled ? (
            <span
              title={user.disabled_reason ?? undefined}
              className="inline-flex items-center gap-1 rounded-full bg-danger-soft/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-danger-soft-fg"
            >
              <Ban className="h-3 w-3" />
              Blocked
            </span>
          ) : null}
        </div>
        <div className="mt-0.5 text-xs text-fg-muted">{user.email}</div>
        {user.disabled && user.disabled_reason ? (
          <div className="mt-1 text-xs text-fg-dim">{user.disabled_reason}</div>
        ) : null}
      </td>
      {isEditing ? (
        <EditCells
          user={user}
          allMobs={allMobs}
          onCancel={onCancel}
          onSaved={onSaved}
        />
      ) : (
        <ReadCells
          user={user}
          allMobs={allMobs}
          isMe={isMe}
          onEdit={onEdit}
          onSaved={onSaved}
        />
      )}
    </tr>
  );
}

function ReadCells({
  user,
  allMobs,
  isMe,
  onEdit,
  onSaved,
}: {
  user: AdminUser;
  allMobs: MobLite[];
  isMe: boolean;
  onEdit: () => void;
  onSaved: (u: AdminUser) => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  async function setBlocked(block: boolean) {
    setBusy(true);
    try {
      const res = await api<{ user: AdminUser }>(
        `/api/admin/users/${user.id}/${block ? 'disable' : 'enable'}`,
        {
          method: 'POST',
          body: JSON.stringify(block ? { reason: reason.trim() || undefined } : {}),
        },
      );
      // The disable/enable responses omit mobilisation_ids; re-attach so the
      // read view stays consistent.
      onSaved({ ...res.user, mobilisation_ids: user.mobilisation_ids });
      toast.success(
        block ? `Blocked ${user.display_name}` : `Unblocked ${user.display_name}`,
      );
      setConfirming(false);
      setReason('');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'action failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <td className="px-4 py-3 align-top">
        {user.roles.length === 0 ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-warning-soft/15 px-2 py-0.5 text-[11px] font-medium text-warning-soft-fg">
            <AlertCircle className="h-3 w-3" />
            no roles
          </span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {user.roles.map((r) => (
              <span
                key={r}
                className="rounded-md bg-surface-2 px-2 py-0.5 text-xs font-medium text-fg-muted"
                title={r}
              >
                {roleLabel(r)}
              </span>
            ))}
          </div>
        )}
      </td>
      <td className="px-4 py-3 align-top">
        <UserMobilisationsRead
          roles={user.roles}
          selected={user.mobilisation_ids}
          all={allMobs}
        />
      </td>
      <td className="px-4 py-3 align-top">
        {user.person_id ? (
          <span className="inline-flex items-center gap-1 text-xs text-fg-muted">
            <Building2 className="h-3 w-3" />
            <code className="font-mono">{user.person_id.slice(0, 8)}</code>
          </span>
        ) : (
          <span className="text-xs text-fg-dim">—</span>
        )}
      </td>
      <td className="px-4 py-3 align-top">
        <span className="inline-flex items-center gap-1 text-xs text-fg-muted">
          <KeyRound className="h-3 w-3" />
          {user.has_password ? 'dev / emergency' : 'SSO'}
        </span>
      </td>
      <td className="px-4 py-3 align-top text-right">
        {confirming ? (
          <div className="flex flex-col items-end gap-2">
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason (optional)"
              aria-label="Reason for blocking"
              className="h-9 w-52 rounded-md border border-line-strong/20 bg-surface px-2.5 text-xs text-fg shadow-sm focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring-strong"
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                iconLeft={<X className="h-3.5 w-3.5" />}
                onClick={() => {
                  setConfirming(false);
                  setReason('');
                }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                size="sm"
                iconLeft={<Ban className="h-3.5 w-3.5" />}
                loading={busy}
                onClick={() => setBlocked(true)}
              >
                Block sign-in
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex justify-end gap-2">
            {user.disabled ? (
              <Button
                variant="secondary"
                size="sm"
                iconLeft={<ShieldCheck className="h-3.5 w-3.5" />}
                loading={busy}
                onClick={() => setBlocked(false)}
              >
                Unblock
              </Button>
            ) : !isMe ? (
              <Button
                variant="ghost"
                size="sm"
                iconLeft={<Ban className="h-3.5 w-3.5" />}
                onClick={() => setConfirming(true)}
              >
                Block
              </Button>
            ) : null}
            <Button
              variant="secondary"
              size="sm"
              iconLeft={<Pencil className="h-3.5 w-3.5" />}
              onClick={onEdit}
            >
              Edit
            </Button>
          </div>
        )}
      </td>
    </>
  );
}

function EditCells({
  user,
  allMobs,
  onCancel,
  onSaved,
}: {
  user: AdminUser;
  allMobs: MobLite[];
  onCancel: () => void;
  onSaved: (u: AdminUser) => void;
}) {
  const roleGroups = useMemo(() => ROLE_GROUPS, []);
  const [roles, setRoles] = useState<Role[]>(user.roles as Role[]);
  const [mobIds, setMobIds] = useState<string[]>(user.mobilisation_ids);
  const [personId, setPersonId] = useState<string>(user.person_id ?? '');
  const [people, setPeople] = useState<PersonLite[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api<{ people: PersonLite[] }>('/api/people');
        if (!cancelled) setPeople(r.people);
      } catch {
        if (!cancelled) setPeople([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const rolesRes = await api<{ user: AdminUser }>(
        `/api/admin/users/${user.id}/roles`,
        { method: 'PUT', body: JSON.stringify({ roles }) },
      );
      const wantedPerson = personId.trim() || null;
      let final = rolesRes.user;
      if (wantedPerson !== (user.person_id ?? null)) {
        const linkRes = await api<{ user: AdminUser }>(
          `/api/admin/users/${user.id}/person`,
          { method: 'PUT', body: JSON.stringify({ person_id: wantedPerson }) },
        );
        final = linkRes.user;
      }
      // Super_admin has implicit all-access; only persist the tick set
      // for other staff, and only when it changed.
      const isSuper = roles.includes('super_admin' as Role);
      let savedMobIds = user.mobilisation_ids;
      if (!isSuper && !sameSet(mobIds, user.mobilisation_ids)) {
        await api<{ user: AdminUser }>(
          `/api/admin/users/${user.id}/mobilisations`,
          { method: 'PUT', body: JSON.stringify({ mobilisation_ids: mobIds }) },
        );
        savedMobIds = mobIds;
      }
      // The roles/person responses omit mobilisation_ids; re-attach so
      // the read view stays consistent after save.
      final = { ...final, mobilisation_ids: savedMobIds };
      toast.success(`Updated ${user.display_name}`);
      onSaved(final);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'save failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <td className="px-4 py-3 align-top">
        <MultiSelect<Role>
          groups={roleGroups}
          value={roles}
          onChange={(next) => setRoles(next)}
          placeholder="Pick one or more roles"
          ariaLabel="User roles"
        />
      </td>
      <td className="px-4 py-3 align-top">
        <UserMobilisationsEdit
          roles={roles}
          value={mobIds}
          onChange={setMobIds}
          all={allMobs}
        />
      </td>
      <td className="px-4 py-3 align-top">
        <div className="relative">
          <select
            value={personId}
            onChange={(e) => setPersonId(e.target.value)}
            className="block h-9 w-full appearance-none rounded-md border border-line-strong/20 bg-surface pl-2.5 pr-8 text-xs text-fg shadow-sm transition-colors hover:bg-surface-2 focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring-strong focus-visible:ring-offset-1"
          >
            <option value="">— none —</option>
            {(people ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.full_name} ({p.email})
              </option>
            ))}
          </select>
          <span
            aria-hidden
            className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-fg-muted"
          >
            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.24 4.38a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
                clipRule="evenodd"
              />
            </svg>
          </span>
        </div>
        {error ? (
          <div className="mt-1 flex items-center gap-1 text-xs text-danger-soft-fg">
            <AlertCircle className="h-3 w-3" />
            {error}
          </div>
        ) : null}
      </td>
      <td className="px-4 py-3 align-top text-xs text-fg-muted">
        <span className="inline-flex items-center gap-1">
          <KeyRound className="h-3 w-3" />
          {user.has_password ? 'dev / emergency' : 'SSO'}
        </span>
      </td>
      <td className="px-4 py-3 align-top text-right">
        <div className="flex justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            iconLeft={<X className="h-3.5 w-3.5" />}
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            iconLeft={<Save className="h-3.5 w-3.5" />}
            loading={saving}
            onClick={save}
          >
            Save
          </Button>
        </div>
      </td>
    </>
  );
}
